import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import MigrationRunLogDialog from '../components/manage/MigrationRunLogDialog';
import { get } from '../utils/api';

function fmtTs(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
}

export default function ManageCiviMigrationLogsPage() {
  const navigate = useNavigate();
  const [q, setQ] = React.useState('');
  const [runId, setRunId] = React.useState('');
  const [level, setLevel] = React.useState('all');
  const [limit, setLimit] = React.useState(200);
  const [autoRefresh, setAutoRefresh] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [filePath, setFilePath] = React.useState('');
  const [total, setTotal] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [runLogOpen, setRunLogOpen] = React.useState(false);
  const [runLogId, setRunLogId] = React.useState('');
  const [runLogQ, setRunLogQ] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/api/manage/civi-wc-product-mapping/migration-logs', {
        q: q || undefined,
        runId: runId || undefined,
        level,
        limit,
      });
      setFilePath(String(res?.file || ''));
      setTotal(Number(res?.totalRecords || 0));
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Failed to load migration logs');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, runId, level, limit]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/manage/civi-wc-product-mapping')} aria-label="Back to Civi mapping" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Civi migration logs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search and monitor selected-record migration logs.
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          label="Search logs"
          placeholder="message text, contribution_id, error…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <TextField
          size="small"
          label="Run ID"
          placeholder="optional exact run id"
          value={runId}
          onChange={(e) => setRunId(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="mig-log-level-label">Level</InputLabel>
          <Select
            labelId="mig-log-level-label"
            label="Level"
            value={level}
            onChange={(e) => setLevel(String(e.target.value || 'all'))}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="mig-log-limit-label">Rows</InputLabel>
          <Select
            labelId="mig-log-limit-label"
            label="Rows"
            value={String(limit)}
            onChange={(e) => setLimit(parseInt(String(e.target.value), 10) || 200)}
          >
            <MenuItem value="100">100</MenuItem>
            <MenuItem value="200">200</MenuItem>
            <MenuItem value="500">500</MenuItem>
            <MenuItem value="1000">1000</MenuItem>
          </Select>
        </FormControl>
        <Button variant={autoRefresh ? 'contained' : 'outlined'} onClick={() => setAutoRefresh((v) => !v)}>
          Auto-refresh: {autoRefresh ? 'On' : 'Off'}
        </Button>
        <Button variant="outlined" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {filePath ? `Log file: ${filePath}` : 'Log file not created yet.'} | Matched rows: {total}
      </Typography>
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: '72vh' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 220 }}>Timestamp</TableCell>
              <TableCell sx={{ width: 90 }}>Level</TableCell>
              <TableCell sx={{ width: 320 }}>Run ID</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>No logs found for current filters.</TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={`log-${r.id}-${r.ts}`} hover>
                <TableCell>{fmtTs(r.ts)}</TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: String(r.level || '').toLowerCase() === 'error' ? 'error.main' : 'primary.main' }}
                  >
                    {String(r.level || 'INFO')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem' }}>
                  {r.runId ? (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => {
                        setRunLogId(String(r.runId));
                        setRunLogQ('');
                        setRunLogOpen(true);
                      }}
                      sx={{ cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}
                    >
                      {String(r.runId)}
                    </Link>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {String(r.message || '')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <MigrationRunLogDialog
        open={runLogOpen}
        onClose={() => setRunLogOpen(false)}
        initialRunId={runLogId}
        initialSearchQ={runLogQ}
      />
    </PageLayout>
  );
}
